import { BigNumber } from "@yjsgames/bignumber";

/**
 * 재생(regen) 벽돌의 주변 회복 — 순수(BigNumber만 의존), 결정론. 피해 경로가 아닌 전투 update tick에서
 * 호출한다(기획 §4). 셸(BattlefieldRunner)은 활성 벽돌에서 좌표/HP를 모아 이 함수들로 대상·회복량을
 * 계산하고, 결과대로 Brick.applyHeal + 회복 FX만 수행한다(Functional Core / Imperative Shell).
 *
 * 정책(기획 §2·§5·§12): 자기 자신 미회복 / 파괴(hp≤0) 미회복 / 이미 maxHp면 미회복 / 회복량은 대상
 * maxHp 비율 / 한 tick에 같은 regen이 같은 대상 1회만(셸이 대상 집합을 한 번에 처리) / 전체 필드 회복 금지(반경 제한).
 */

/** nowMs 기준 회복 주기 도달 여부 — `now − lastHeal >= interval`(경계 포함). */
export function shouldRegenTick(nowMs: number, lastHealMs: number, intervalMs: number): boolean {
  return nowMs - lastHealMs >= intervalMs;
}

/** 회복 대상 후보(셸이 활성 벽돌에서 추출). */
export interface RegenTargetState {
  readonly gridX: number;
  readonly gridY: number;
  readonly hp: BigNumber;
  readonly maxHp: BigNumber;
}

/**
 * regen 벽돌 주변(Chebyshev 거리 ≤ radius)에서 회복 대상 인덱스를 선택. 제외: 자기 자신(같은 grid 좌표),
 * 파괴(hp≤0), 이미 풀피(hp≥maxHp). 반경 1이면 인접 8칸이 대상(기획 □♻□ 3×3).
 */
export function selectRegenTargets(
  regen: { readonly gridX: number; readonly gridY: number },
  radius: number,
  targets: readonly RegenTargetState[],
): number[] {
  const out: number[] = [];
  for (let i = 0; i < targets.length; i++) {
    const t = targets[i]!;
    if (t.gridX === regen.gridX && t.gridY === regen.gridY) continue; // 자기 자신
    const cheb = Math.max(Math.abs(t.gridX - regen.gridX), Math.abs(t.gridY - regen.gridY));
    if (cheb > radius) continue; // 반경 밖
    if (!t.hp.gt(BigNumber.ZERO)) continue; // 파괴됨
    if (t.hp.gte(t.maxHp)) continue; // 이미 풀피
    out.push(i);
  }
  return out;
}

/** 1회 회복량 = max(1, maxHp × ratio). (BigNumber라 정수 floor 불필요 — 대규모 스케일에서 무의미.) */
export function regenHealAmount(maxHp: BigNumber, ratio: number): BigNumber {
  return maxHp.mul(BigNumber.fromNumber(ratio)).max(BigNumber.ONE);
}

/** 회복 적용(상한 maxHp 클램프) — 회복 후 HP = min(maxHp, hp + heal). */
export function applyHealCap(hp: BigNumber, maxHp: BigNumber, heal: BigNumber): BigNumber {
  return hp.add(heal).min(maxHp);
}

/** regen 벽돌 1개의 회복 원천 상태(셸이 활성 regen 벽돌에서 추출). */
export interface RegenSourceState {
  readonly gridX: number;
  readonly gridY: number;
  readonly healRadius: number;
  readonly healAmountRatio: number;
  readonly lastHealMs: number;
  readonly healIntervalMs: number;
}

/** 한 대상 회복 이벤트 — 셸이 targetIndex 벽돌에 newHp 적용 + amount만큼 +HP FX. */
export interface RegenHealEvent {
  /** targets 배열 인덱스. */
  readonly targetIndex: number;
  /** 회복 후 HP(클램프 완료). */
  readonly newHp: BigNumber;
  /** 회복량(+HP 텍스트 표시용). */
  readonly amount: BigNumber;
}

/** interval 도달한 regen 1개의 회복 계획 — heals가 비어도(대상 0) 결과에 포함(셸이 lastHealMs 갱신). */
export interface RegenTickResult {
  /** regens 배열 인덱스. */
  readonly regenIndex: number;
  readonly heals: readonly RegenHealEvent[];
}

/**
 * 전 regen 벽돌의 이번 tick 회복 계획을 순수 계산 — 셸(BattlefieldRunner)은 결과대로 applyHeal + FX만.
 * interval 도달(shouldRegenTick) regen만 처리하고, 로컬 HP 복사본을 순차 갱신해 **stacking 정합**을 보장한다
 * (앞 regen의 회복이 다음 regen의 대상 선택·회복량에 반영 — 같은 tick 다중 regen이 같은 대상 회복 가능, 기획 §5).
 * regens·targets는 별도 배열이고 regen은 보통 targets의 부분집합 — 인덱스로 셸이 원 벽돌을 역참조한다.
 */
export function planRegenHeals(
  regens: readonly RegenSourceState[],
  targets: readonly RegenTargetState[],
  nowMs: number,
): RegenTickResult[] {
  const liveHp = targets.map((t) => t.hp); // 순차 시뮬용 로컬 HP 복사본
  const results: RegenTickResult[] = [];
  for (let ri = 0; ri < regens.length; ri++) {
    const r = regens[ri]!;
    if (!shouldRegenTick(nowMs, r.lastHealMs, r.healIntervalMs)) continue;
    const snapshot = targets.map((t, i) => ({ gridX: t.gridX, gridY: t.gridY, hp: liveHp[i]!, maxHp: t.maxHp }));
    const idxs = selectRegenTargets({ gridX: r.gridX, gridY: r.gridY }, r.healRadius, snapshot);
    const heals: RegenHealEvent[] = [];
    for (const i of idxs) {
      const amount = regenHealAmount(targets[i]!.maxHp, r.healAmountRatio);
      const newHp = applyHealCap(liveHp[i]!, targets[i]!.maxHp, amount);
      liveHp[i] = newHp; // 다음 regen이 이 회복 결과를 본다(stacking)
      heals.push({ targetIndex: i, newHp, amount });
    }
    results.push({ regenIndex: ri, heals });
  }
  return results;
}
